/**
 * UspTicker — shows one row at a time and scrolls the next one up.
 *
 * Shared by the PDP purchase area (`_pdp-usps`) and the header USP banner
 * (`header-announcements`). The host is a one-row-tall viewport (`overflow: hidden`) over a column
 * track. Advancing animates the track up by exactly one row with the Web Animations API, and when
 * that finishes the first row is moved to the end of the track. The DOM order rotates, so the
 * resting state after the move is pixel-identical to the animated end state — no clones, no offset
 * bookkeeping, and the rotation is infinite by construction.
 *
 * Row height is measured rather than assumed: rows wrap on a narrow info column, so the viewport
 * is pinned to the tallest row via `--usp-ticker-height` and every row is stretched to match it by
 * CSS. Measuring clears the variable first so a row that got shorter can shrink the ticker.
 *
 * Rotation is suspended while the customer is reading or interacting — hover, keyboard focus, a
 * hidden tab — because a row can contain a link, and a link that slides away mid-click is a trap.
 *
 * DOM contract:
 *   [data-usp-track]      — the column that gets translated
 *   [data-usp-item]       — one row
 *   [data-interval]       — milliseconds each row stays visible
 *   [data-reduced-motion] — behaviour under `prefers-reduced-motion: reduce`:
 *                           'stack' (default) never rotates and leaves the rows to the
 *                           stylesheet, which stacks them; 'rotate' keeps cycling but swaps
 *                           instantly instead of sliding, for hosts with a fixed height that
 *                           cannot grow to fit every row at once.
 */

const ANIMATION_DURATION = 500;
const ANIMATION_EASING = "cubic-bezier(0.35, 0, 0.15, 1)";
const DEFAULT_INTERVAL = 2000;

class UspTicker extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | undefined} */
  #timer;

  /** @type {Animation | undefined} */
  #animation;

  /** @type {number} */
  #rowHeight = 0;

  /** @type {number} */
  #lastWidth = 0;

  /** Set while hovered, focused, backgrounded or selected in the theme editor. */
  #suspended = false;

  connectedCallback() {
    this.#measure();

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);

    this.addEventListener("pointerenter", this.#suspend);
    this.addEventListener("pointerleave", this.#resume);
    this.addEventListener("focusin", this.#suspend);
    this.addEventListener("focusout", this.#resume);
    document.addEventListener("visibilitychange", this.#handleVisibilityChange);
    document.addEventListener("shopify:block:select", this.#handleBlockSelect);
    document.addEventListener("shopify:block:deselect", this.#handleBlockDeselect);

    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#animation?.cancel();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.removeEventListener("pointerenter", this.#suspend);
    this.removeEventListener("pointerleave", this.#resume);
    this.removeEventListener("focusin", this.#suspend);
    this.removeEventListener("focusout", this.#resume);
    document.removeEventListener("visibilitychange", this.#handleVisibilityChange);
    document.removeEventListener("shopify:block:select", this.#handleBlockSelect);
    document.removeEventListener("shopify:block:deselect", this.#handleBlockDeselect);
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-usp-track]"));
  }

  /** @returns {HTMLElement[]} */
  get #rows() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll("[data-usp-item]")]);
  }

  get #interval() {
    const interval = Number.parseInt(this.dataset.interval ?? "", 10);
    return Number.isNaN(interval) ? DEFAULT_INTERVAL : interval;
  }

  get #prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /** @returns {'stack' | 'rotate'} */
  get #reducedMotionMode() {
    return this.dataset.reducedMotion === "rotate" ? "rotate" : "stack";
  }

  #start() {
    this.#stop();
    // One row has nothing to rotate to.
    if (this.#rows.length < 2 || this.#suspended) return;
    // In 'stack' mode reduced motion is served by the stacked CSS layout instead.
    if (this.#prefersReducedMotion && this.#reducedMotionMode === "stack") return;
    this.#timer = window.setInterval(this.#advance, this.#interval);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #suspend = () => {
    this.#suspended = true;
    this.#stop();
  };

  #resume = () => {
    this.#suspended = false;
    this.#start();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#suspend();
    else this.#resume();
  };

  /**
   * Only width changes matter. The height of the host is set by this element itself, so
   * remeasuring on it would loop.
   * @param {ResizeObserverEntry[]} entries
   */
  #handleResize = (entries) => {
    const width = Math.round(entries[0]?.contentRect.width ?? 0);
    if (width === this.#lastWidth) return;
    this.#lastWidth = width;
    this.#measure();
  };

  #measure() {
    const rows = this.#rows;
    if (!rows.length) return;

    // Rows are stretched to `--usp-ticker-height`, so measuring with it still applied would only
    // ever report the previous maximum and the ticker could never shrink.
    this.style.removeProperty("--usp-ticker-height");

    const height = Math.ceil(Math.max(...rows.map((row) => row.getBoundingClientRect().height)));
    if (!height) return;

    this.#rowHeight = height;
    this.style.setProperty("--usp-ticker-height", `${height}px`);
  }

  #advance = () => {
    const track = this.#track;
    const first = track?.firstElementChild;
    if (!track || !first || !this.#rowHeight) return;

    this.#animation?.cancel();
    this.#animation = track.animate(
      { transform: ["translateY(0)", `translateY(-${this.#rowHeight}px)`] },
      {
        // 'rotate' under reduced motion still cycles the rows, it just cuts instead of sliding.
        duration: this.#prefersReducedMotion ? 0 : ANIMATION_DURATION,
        easing: ANIMATION_EASING
      }
    );

    this.#animation.finished
      .then(() => {
        // Moving the row that just scrolled out to the end restores exactly the frame the
        // animation ended on, so dropping the animation's fill is invisible.
        track.append(first);
      })
      .catch(() => {
        // Cancelled by a newer advance or by disconnect — nothing to clean up.
      });
  };

  /**
   * Bring the row a merchant selected in the theme editor into view and hold it there.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const row = target?.closest?.("[data-usp-item]");
    if (!row || !this.contains(row)) return;

    this.#suspend();

    const track = this.#track;
    if (!track) return;

    this.#animation?.cancel();
    // Rotate rather than scroll: the selected row becomes the first one with no animation.
    for (let i = 0; i < this.#rows.length && track.firstElementChild !== row; i += 1) {
      const first = track.firstElementChild;
      if (!first) break;
      track.append(first);
    }
  };

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const row = target?.closest?.("[data-usp-item]");
    if (!row || !this.contains(row)) return;
    this.#resume();
  };
}

if (!customElements.get("usp-ticker")) {
  customElements.define("usp-ticker", UspTicker);
}
